// La BASE de la pizza: salsa, masa y tamaño.
//
// Son las tres decisiones que describen la pizza ENTERA y no se ponen en pedacitos
// encima. Antes el dibujo las ignoraba —elegir salsa blanca dejaba la pizza roja, y
// una familiar se veía igual que una mediana— y encima las pintaba como ingredientes:
// "Normal", "Delgada" y "Roja" terminaban de confeti sobre una Margherita que solo
// lleva queso y salsa.
//
// El nombre lo escribe el dueño, así que se reconoce por patrón y con `null` cuando
// no se reconoce: sin salsa identificada se dibuja la roja de siempre, que es el
// comportamiento anterior.

import { normalizar } from "@/lib/menu/pizzaIngredients"

export type Salsa = "roja" | "blanca" | "bbq" | "ninguna"
export type Masa = "delgada" | "gruesa" | "rellena"
export type NivelQueso = "normal" | "doble" | "sin"

/** Colores de cada salsa. Se exportan para que el dibujo no los invente. */
export const COLOR_SALSA: Record<Salsa, { claro: string; oscuro: string }> = {
  // Saturación de foto de carta, no de témpera: el rojo tira a naranja quemado.
  roja:   { claro: "#E04B28", oscuro: "#9C2412" },
  blanca: { claro: "#F3E9D8", oscuro: "#D9C6A6" },
  bbq:    { claro: "#8A4A1E", oscuro: "#4E250C" },
  // "Sin salsa": queda la masa a la vista, apenas untada de aceite.
  ninguna: { claro: "#E8C88E", oscuro: "#C79A57" },
}

export function salsaDe(nombre: string): Salsa | null {
  const n = normalizar(nombre)
  if (/sin salsa|no salsa|sin sauce/.test(n)) return "ninguna"
  if (/bbq|barbacoa|barbicue/.test(n)) return "bbq"
  if (/blanca|bechamel|alfredo|crema|white/.test(n)) return "blanca"
  if (/roja|tomate|marinara|napolitana|pomodoro|red/.test(n)) return "roja"
  return null
}

export function masaDe(nombre: string): Masa | null {
  const n = normalizar(nombre)
  if (/rellena|stuffed|borde de queso/.test(n)) return "rellena"
  if (/gruesa|gorda|pan|thick|siciliana/.test(n)) return "gruesa"
  if (/delgada|fina|thin|crocante|napolitana/.test(n)) return "delgada"
  return null
}

export function nivelQuesoDe(nombre: string): NivelQueso | null {
  const n = normalizar(nombre)
  if (/sin queso|no queso|sin cheese/.test(n)) return "sin"
  if (/doble|extra|triple/.test(n)) return "doble"
  if (/normal|regular/.test(n)) return "normal"
  return null
}

/**
 * Grosor del borde, en unidades del lienzo de 320.
 *
 * La diferencia tiene que ser VISIBLE a un par de metros —es lo que el cliente está
 * comprobando— pero sin que la gruesa parezca otra comida.
 */
export const BORDE: Record<Masa, number> = {
  delgada: 11,
  gruesa: 22,
  rellena: 26,
}

/**
 * Cuánto ocupa cada tamaño dentro del mismo recuadro.
 *
 * 🔴 NO es proporcional al diámetro real (30/35/40 cm daría 0.75/0.88/1.0): a esa
 * escala la mediana se ve enana y ridícula en una pantalla donde es la protagonista.
 * Se comprime el rango para que la diferencia se NOTE sin que la chica se vea pobre.
 *
 * El orden lo da la posición en la lista de tamaños, no el nombre: una pizzería puede
 * llamarlos "Personal / Mediana / Party" y el patrón no los reconocería.
 */
export function escalaDeTamano(indice: number, total: number): number {
  if (total <= 1) return 1
  const k = indice / (total - 1) // 0 = el más chico, 1 = el más grande
  return 0.84 + k * 0.16
}
